import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MONTH_SHORT, TARGET_METRICS, monthsIn, type TargetMetric, type TargetPeriodType } from '@mfs/shared';
import { salesDashboardApi, type TargetInput } from '../lib/salesDashboardApi';

/**
 * Admin → Sales data → Targets: a year of targets for one metric and scope,
 * set against last year's actuals. Month targets are the usual thing to set;
 * a quarter or the year with no target of its own is the sum of its months
 * (shown in grey), and one set explicitly wins.
 */

const METRIC_LABELS: Record<TargetMetric, string> = { net_sales: 'Net sales', orders: 'Orders', units: 'Units' };

interface Row {
  type: TargetPeriodType;
  start: string;
  label: string;
}

function rowsFor(year: number): Row[] {
  const months = MONTH_SHORT.map((m, i) => ({ type: 'month' as const, start: `${year}-${String(i + 1).padStart(2, '0')}-01`, label: m }));
  const quarters = [1, 2, 3, 4].map((q) => ({ type: 'quarter' as const, start: `${year}-${String((q - 1) * 3 + 1).padStart(2, '0')}-01`, label: `Q${q}` }));
  return [...months, ...quarters, { type: 'year', start: `${year}-01-01`, label: String(year) }];
}

const cellKey = (type: TargetPeriodType, start: string) => `${type}|${start}`;
const shiftYear = (start: string, years: number) => `${Number(start.slice(0, 4)) + years}${start.slice(4)}`;

export function AdminSalesTargets() {
  const client = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [metric, setMetric] = useState<TargetMetric>('net_sales');
  const [scopeKey, setScopeKey] = useState('combined');
  const [growth, setGrowth] = useState(10);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const query = useQuery({ queryKey: ['sales-targets', year], queryFn: () => salesDashboardApi.targets(year) });
  const data = query.data;
  const money = metric === 'net_sales';

  /** What is stored, as the text an input would show (dollars for money). */
  const stored = useMemo(() => {
    const out: Record<string, string> = {};
    for (const t of data?.targets ?? []) {
      if (t.metric !== metric || t.scopeKey !== scopeKey) continue;
      out[cellKey(t.periodType, t.periodStart)] = String(money ? t.value / 100 : t.value);
    }
    return out;
  }, [data, metric, scopeKey, money]);

  useEffect(() => setDraft(stored), [stored]);

  const actual = (start: string, type: TargetPeriodType): number | null => {
    const months = monthsIn(type, start);
    const byMonth = data?.actuals[scopeKey] ?? {};
    let sum = 0;
    let any = false;
    for (const m of months) {
      const a = byMonth[m];
      if (a) {
        sum += a[metric];
        any = true;
      }
    }
    return any ? sum : null;
  };

  const valueOf = (text: string | undefined): number | null => {
    if (text === undefined || text.trim() === '') return null;
    const n = Number(text.replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n >= 0 ? Math.round(money ? n * 100 : n) : null;
  };

  /** A quarter or year with none set shows the sum of its months, when all are set. */
  const derived = (row: Row): number | null => {
    if (row.type === 'month') return null;
    let sum = 0;
    for (const m of monthsIn(row.type, row.start)) {
      const v = valueOf(draft[cellKey('month', m)]);
      if (v === null) return null;
      sum += v;
    }
    return sum;
  };

  const fmt = (value: number | null) =>
    value === null
      ? '—'
      : money
        ? (value / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
        : value.toLocaleString('en-US');

  const changed = Object.keys({ ...stored, ...draft }).filter((k) => (stored[k] ?? '') !== (draft[k] ?? ''));
  // Text that is not a number must not be saved as "no target".
  const invalid = changed.filter((k) => (draft[k] ?? '').trim() !== '' && valueOf(draft[k]) === null);

  const save = useMutation({
    mutationFn: () =>
      salesDashboardApi.saveTargets(
        changed.map((k): TargetInput => {
          const [periodType, periodStart] = k.split('|') as [TargetPeriodType, string];
          return { metric, periodType, periodStart, scopeKey, value: valueOf(draft[k]) };
        }),
      ),
    onSuccess: (r) => {
      setResult({ ok: true, message: `Saved ${r.saved} target${r.saved === 1 ? '' : 's'}` });
      void client.invalidateQueries({ queryKey: ['sales-targets'] });
      void client.invalidateQueries({ queryKey: ['sales', 'pace'] });
    },
    onError: (e: Error) => setResult({ ok: false, message: e.message }),
  });

  const fillFromLastYear = () => {
    const next = { ...draft };
    for (const row of rowsFor(year).filter((r) => r.type === 'month')) {
      const last = actual(shiftYear(row.start, -1), 'month');
      if (last === null) continue;
      const raw = last * (1 + growth / 100);
      // Round to something a person would have typed: $100 for money, whole units otherwise.
      next[cellKey('month', row.start)] = String(money ? Math.round(raw / 10_000) * 100 : Math.round(raw));
    }
    setDraft(next);
  };

  const rows = rowsFor(year);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Targets</h3>
      <p className="small muted" style={{ marginTop: 0 }}>
        Targets drive the pace and forecast widgets, the target insights and the monthly email. Set months; a quarter or the year
        without its own target is the sum of its months.
        {data && data.notImporting.length > 0 && (
          <> Actuals include only channels whose orders are imported ({data.importing.join(', ') || 'none yet'}).</>
        )}
      </p>
      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select value={metric} onChange={(e) => setMetric(e.target.value as TargetMetric)}>
          {TARGET_METRICS.map((m) => (
            <option key={m} value={m}>
              {METRIC_LABELS[m]}
            </option>
          ))}
        </select>
        <select value={scopeKey} onChange={(e) => setScopeKey(e.target.value)}>
          {(data?.scopes ?? [{ key: 'combined', label: 'All sales' }]).map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <span className="spacer" />
        <span className="small muted">Fill months from last year +</span>
        <input type="number" value={growth} onChange={(e) => setGrowth(Number(e.target.value))} style={{ width: 64 }} />
        <span className="small muted">%</span>
        <button onClick={fillFromLastYear} disabled={!data}>
          Fill
        </button>
      </div>
      {query.error && <div className="alert error">{(query.error as Error).message}</div>}
      {data && (
        <div className="table-wrap" style={{ maxHeight: 'none' }}>
          <table className="grid compact targets-grid">
            <thead>
              <tr>
                <th>Period</th>
                <th className="align-right">{year - 1} actual</th>
                <th className="align-right">{year} actual</th>
                <th className="align-right">Target{money ? ' ($)' : ''}</th>
                <th className="align-right">Actual vs target</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const key = cellKey(row.type, row.start);
                const target = valueOf(draft[key]) ?? derived(row);
                const now = actual(row.start, row.type);
                const isSum = valueOf(draft[key]) === null && derived(row) !== null;
                return (
                  <tr key={key} className={row.type !== 'month' ? 'targets-total' : ''}>
                    <td>{row.label}</td>
                    <td className="align-right num muted">{fmt(actual(shiftYear(row.start, -1), row.type))}</td>
                    <td className="align-right num">{fmt(now)}</td>
                    <td className="align-right">
                      <input
                        className="num"
                        inputMode="decimal"
                        value={draft[key] ?? ''}
                        placeholder={isSum ? `${fmt(derived(row))} (months)` : ''}
                        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                        style={{ width: 150, textAlign: 'right' }}
                        aria-label={`${row.label} target`}
                      />
                    </td>
                    <td className="align-right num">
                      {target && now !== null ? (
                        <span className={now >= target ? 'ok-text' : ''}>{Math.round((now / target) * 100)}%</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
        {invalid.length > 0 && <span className="small warn-text">Not a number: {invalid.map((k) => rows.find((r) => cellKey(r.type, r.start) === k)?.label).join(', ')}</span>}
        <button className="primary" disabled={changed.length === 0 || invalid.length > 0 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : changed.length > 0 ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'Saved'}
        </button>
        {changed.length > 0 && (
          <button onClick={() => setDraft(stored)} disabled={save.isPending}>
            Undo changes
          </button>
        )}
      </div>
    </div>
  );
}
